import { Avatar, Box, Button, Card, Chip, Divider, Typography } from "@mui/material";
import { icons } from "../../../assets";
import dayjs from 'dayjs'; // Import dayjs for date calculations

// Define a proper interface for the component's props
interface SideProfileInfoProps {
  img?: string;
  name?: string;
  location?: string;
  onClick?: () => void;
  designation?: string;
  number?: string | number;
  email?: string;
  experience?: string | number;
  dob?: string; // Expecting string like "YYYY-MM-DD" or "DD-MM-YYYY" etc.
  gender?: string;
  languages?: string[]; // Assuming languages is an array of strings
  services?: Array<string | { title: string }>; // Services can be string array or object array
}

export const SideProfileInfo = ({
  img,
  name,
  location,
  onClick,
  designation,
  number,
  email,
  experience,
  dob,
  gender,
  // languages, // languages prop is not currently used in the JSX
  services,
}: SideProfileInfoProps) => {

  const calculateAge = (birthDateString?: string): number | null => {
    if (!birthDateString) return null;

    // Attempt to parse common formats. Adjust if your DOB string is consistently different.
    let birthDate = dayjs(birthDateString); // Handles ISO "YYYY-MM-DD"
    if (!birthDate.isValid()) {
      birthDate = dayjs(birthDateString, "DD-MM-YYYY"); // Handles "DD-MM-YYYY"
    }
    if (!birthDate.isValid()) {
      birthDate = dayjs(birthDateString, "MM-DD-YYYY"); // Handles "MM-DD-YYYY"
    }
    // Add more formats if necessary
    // Example: birthDate = dayjs(birthDateString, "YYYY/MM/DD");

    if (birthDate.isValid()) {
      return dayjs().diff(birthDate, 'year');
    }
    console.warn(`Could not parse DOB string: "${birthDateString}" to calculate age.`);
    return null;
  };

  const age = calculateAge(dob);

  // Format DOB for display, e.g., to DD-MM-YYYY
  // Ensure dob is a valid date string before attempting to format
  let displayDob = "Not specified";
  if (dob) {
    const parsedDisplayDob = dayjs(dob); // Try ISO first
    if (parsedDisplayDob.isValid()) {
      displayDob = parsedDisplayDob.format("DD-MM-YYYY");
    } else {
      const parsedDisplayDobAlt = dayjs(dob, "DD-MM-YYYY"); // Try DD-MM-YYYY
      if (parsedDisplayDobAlt.isValid()) {
        displayDob = parsedDisplayDobAlt.format("DD-MM-YYYY");
      } else {
        // If still not valid, display the original string or a fallback
        // This handles cases where the dob might already be formatted or is invalid
        displayDob = dob; 
      }
    }
  }


  return (
    <>
      <Card
        sx={{
          padding: "40px 30px 20px 30px",
        }}
      >
        <Box sx={{ textAlign: "center", margin: "auto", width: "fit-content" }}> {/* Changed width for better centering */}
          <Avatar
            variant="rounded"
            sx={{ height: "142px", width: "142px", fontSize: "100px", margin: "0 auto" }}
            src={img}
            alt={name || "Profile"} // Added fallback for alt text
          />
          <Typography variant="h3" m="15px 0 10px 0">
            {name || "N/A"}
          </Typography>
          <Box display="flex" flexDirection="column" alignItems="center"> {/* Center items in this box */}
            <Typography variant="caption">{location || "Not specified"}</Typography>
            {onClick && ( // Conditionally render button if onClick is provided
              <Button
                sx={{ marginTop: "10px" }}
                variant="contained"
                onClick={onClick}
              >
                Edit Profile
              </Button>
            )}
          </Box>
        </Box>
        <Box py="20px">
          <Divider />
          <Box display="flex" mt={2} alignItems="center">
            <img src={icons.graduateIcn} alt="graduate" height="16px" style={{ marginRight: '8px' }} />
            <Typography>{designation || "Not specified"}</Typography>
          </Box>
          <Box display="flex" mt={2} alignItems="center">
            <img src={icons.mobileIcn} alt="mobile" height="16px" style={{ marginRight: '8px' }} />
            <Typography>{number || "Not specified"}</Typography>
          </Box>
          <Box display="flex" mt={2} alignItems="center">
            <img src={icons.emailIcn} alt="email" height="16px" style={{ marginRight: '8px' }} />
            <Typography>{email || "Not specified"}</Typography>
          </Box>
          <Box display="flex" mt={2} alignItems="center">
            <img src={icons.experienceIcn} alt="experience" height="16px" style={{ marginRight: '8px' }} />
            <Typography>{experience ? `${experience} Year Experience` : "Not specified"}</Typography>
          </Box>
          <Box display="flex" mt={2} alignItems="center">
            <img src={icons.dobIcn} alt="dob" height="16px" style={{ marginRight: '8px' }} />
            <Typography>{displayDob}</Typography>
          </Box>
          {/* Display Age */}
          {age !== null && (
            <Box display="flex" mt={2} alignItems="center">
              {/* Using a placeholder for icon alignment. Replace with an actual age icon if available. */}
              <img src={icons.dobIcn} alt="age" height="16px" style={{ marginRight: '8px', opacity: 0.6 }} /> {/* Re-using DOB icon slightly dimmed or use a different one */}
              <Typography>{age} Years</Typography>
            </Box>
          )}
          <Box display="flex" mt={2} alignItems="center">
            <img src={icons.genderIcn} alt="gender" height="16px" style={{ marginRight: '8px' }} />
            <Typography>{gender || "Not specified"}</Typography>
          </Box>
          {/* Services Section Updated */}
          <Box display="flex" mt={2} alignItems="flex-start">
            <img
              src={icons.serviceIcon}
              alt="services"
              height="16px"
              style={{ marginRight: '8px', flexShrink: 0, marginTop: '4px' }}
            />
            <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ flexGrow: 1 }}>
              {services && services.length > 0 ? (
                services.map((item, index) => {
                  const serviceLabel = typeof item === 'string' ? item : item?.title;
                  if (!serviceLabel) {
                    return null;
                  }
                  return (
                    <Chip
                      key={index}
                      label={serviceLabel}
                      size="small"
                      variant="outlined"
                    />
                  );
                })
              ) : (
                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', lineHeight: '24px' }}>
                  Not specified
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Card>
    </>
  );
};
